/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';
import {
  FlightStatusForm,
  FlightStatusFormData
} from '@com-delta-idp/mnx-capability-flight-status-ui';
import {
  AirportSearchErrorService,
  FlightStatusAem,
  HeaderFooterMachCore,
  HeaderFooterMachCoreContentService,
  LoggingService,
  RetrieveSearchService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { GenericAction } from '@com-delta-omnichannel/mach';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';
import { FlightStatusAdapterService } from '../adapters/flight-status-adapter.service';
import { predictiveCitiesAdapter } from '../adapters/flight-status.adapter';
import { FLIGHT_STATUS_SMART_CONSTANTS } from '../models/constants/flight-status.constants';
import { CityCodeResult } from '../models/interface/retrieve-search.interface';
import { FlightStatusUrlHelperService } from '../services/flight-status-url.helper.service';

@Injectable({
  providedIn: 'root'
})
export class FlightStatusFacadeService {
  flightStatusContent$ = new BehaviorSubject<FlightStatusForm | null>(null);
  predictiveCities: GenericAction[] = [];
  airportSearchErrorService = inject(AirportSearchErrorService);
  onDataReady: (() => void) | null = null;
  flightSubmitUrl = '';
  formUrl = '';
  flightScheduleSubmitUrl: string | undefined;
  flightStatusSubmitUrl: string | undefined;
  spinner = false;
  content = inject(HeaderFooterMachCoreContentService);
  retrieveSearchService = inject(RetrieveSearchService);
  urlHelperService = inject(FlightStatusUrlHelperService);
  loggingService = inject(LoggingService);
  adapter = inject(FlightStatusAdapterService);

  init(callback?: (success: boolean) => void): void {
    this.spinner = true;

    this.content
      .getHeaderFooterMachCoreContent$()
      .pipe(
        take(1),
        catchError((err) => {
          this.loggingService.log(
            `Flight Status content loading failed: ${err?.message ?? err}`
          );
          return of(null);
        }),
        finalize(() => {
          this.spinner = false;
        })
      )
      .subscribe((aemResponse) => {
        this.handleAemResponse(aemResponse, callback);
        const errorMsg =
          aemResponse?.header?.flight_status?.error
            ?.predictiveCitiesNoResults || '';
        this.airportSearchErrorService.setErrorMessageFromAEM(errorMsg);
      });
  }

  private handleAemResponse(
    aemResponse: HeaderFooterMachCore | null,
    callback?: (success: boolean) => void
  ): void {
    const success = !!aemResponse?.header?.flight_status;
    if (success && aemResponse) {
      this.processContent(aemResponse.header.flight_status);
    } else {
      this.adapter.setErrorMessage(
        FLIGHT_STATUS_SMART_CONSTANTS.ERROR_MESSAGES.AEM_CONTENT_UNAVAILABLE
      );
      this.flightStatusContent$.next(null);
      this.loggingService.log(
        'Flight Status content not available in AEM response'
      );
    }
    callback?.(success);
  }

  private processContent(flightStatusAem: FlightStatusAem): void {
    try {
      const content = this.adapter.getContent(flightStatusAem);
      this.flightStatusContent$.next(content);
      this.flightSubmitUrl = flightStatusAem.submitUrl;
      this.flightStatusSubmitUrl = flightStatusAem.flightStatusSubmitUrl;
      this.flightScheduleSubmitUrl = flightStatusAem.flightScheduleSubmitUrl;
    } catch (error) {
      this.loggingService.log(`Content processing failed: ${error}`);
      this.adapter.setErrorMessage(
        FLIGHT_STATUS_SMART_CONSTANTS.ERROR_MESSAGES.AEM_CONTENT_UNAVAILABLE
      );
      this.flightStatusContent$.next(null);
    }
  }

  searchForAirport(searchTerm: string): Subscription {
    this.airportSearchErrorService.clearError();
    return this.retrieveSearchService
      .getPredictiveCities(`/${searchTerm}`)
      .pipe(take(1))
      .subscribe({
        next: (response: CityCodeResult[]) => {
          this.predictiveCities = predictiveCitiesAdapter(response);
          this.airportSearchErrorService.handleEmptyResults(
            response.length,
            searchTerm
          );
          this.onDataReady?.();
        },
        error: (error) => {
          this.loggingService.log(
            JSON.stringify({
              message:
                FLIGHT_STATUS_SMART_CONSTANTS.ERROR_MESSAGES
                  .PREDICTIVE_CITIES_ERROR,
              error: error
            })
          );
          this.predictiveCities = [];
          this.airportSearchErrorService.handleApiError(searchTerm);
          this.onDataReady?.();
        }
      });
  }

  loadFlightStatus(flightDetails: FlightStatusFormData): void {
    const flightDate = this.urlHelperService.getFlightDate(flightDetails);
    const url = flightDetails?.flightNumber
      ? this.urlHelperService.redirectToFlightNoUrl(
          flightDetails,
          flightDate,
          this.flightSubmitUrl,
          this.flightStatusSubmitUrl
        )
      : flightDetails?.origin && flightDetails?.destination
      ? this.urlHelperService.redirectToOriginDestinationUrl(
          flightDetails,
          flightDate,
          this.flightSubmitUrl,
          this.flightScheduleSubmitUrl
        )
      : '';

    this.formUrl = url;
  }
}
